// Mock data for Sania Srivastava's Portfolio

export const personalInfo = {
  name: "Sania Srivastava",
  title: "Technical Project Manager / AM - Airtable Solutions",
  tagline: "Product • Strategy • Technology",
  location: "London, UK",
  email: "sania.srivastav07@gmail.com",
  linkedin: "https://www.linkedin.com/in/sania-srivastava-80291719",
  cvUrl: "/assets/Sania_Srivastava_CV.pdf",
  bio: "I sit where technology meets strategy - turning complex business requirements into structured project plans, seamless solutions, and outcomes that actually ship. Based in London. With a foundation in Computer Science and a Master's in Business Management, I bring both technical understanding and commercial awareness to product delivery conversations.",
  photo: "/sania.jpg"
};

export const stats = [
  { value: "50+", label: "Solutions Delivered", icon: "Boxes" },
  { value: "80%", label: "Process Automation", icon: "Zap" },
  { value: "20+", label: "Projects Managed", icon: "ClipboardList" },
  { value: "10+", label: "Industries Served", icon: "Building2" }
];

export const experience = [
  {
    id: "exp-0",
    company: "Optimize IS",
    role: "Technical AM / Project Manager",
    period: "April 2026 - Present",
    location: "London, United Kingdom",
    type: "Full-time",
    description: "Managing the full project lifecycle for end-to-end Airtable implementations - translating business and technical requirements into structured plans, leading discovery, and shipping solutions across multiple concurrent client engagements.",
    highlights: [
      "Translated complex business and technical requirements into detailed project plans, milestones, and deliverables for end-to-end Airtable implementations across multiple concurrent client engagements",
      "Led technical discovery sessions to uncover client use cases, integration needs, and operational requirements, producing structured requirements documentation, process maps, and scopes of work",
      "Managed project timelines, priorities, and resource allocations across active engagements, proactively identifying and mitigating risks to keep delivery on track and within budget",
      "Coordinated user acceptance testing, issue tracking, and resolution processes to ensure all solutions met technical standards and client requirements prior to go-live",
      "Managed stakeholder communication across both technical and non-technical functions throughout each project lifecycle, maintaining alignment on requirements, decisions, and delivery outcomes",
      "Contributed to pre-sales technical scoping calls and solution design discussions, supporting the development of solution architecture documentation aligned to client objectives",
      "Identified and implemented improvements to delivery processes and documentation standards, contributing to continuous improvement across the project management function"
    ],
    tags: ["Airtable", "Technical PM", "Solution Architecture", "Discovery", "UAT"]
  },
  {
    id: "exp-1",
    company: "Zeus",
    role: "Business Solutions Specialist",
    period: "Jan 2025 - March 2026",
    location: "London, UK",
    type: "Full-time",
    description: "Leading solution design and enablement, collaborating with product, technical, and commercial teams across 10+ multinational companies.",
    highlights: [
      "Partnered with product, operations and technical teams to capture requirements, map workflows, and translate business needs into wireframes, playbooks and delivery outputs across mobility, retail and logistics clients",
      "Supported discovery workshops and demos using synthetic models/digital twins to help stakeholders visualise target workflows, validate feasibility, and identify operational gaps early",
      "Created internal playbooks and operational blueprints to standardise delivery and improve product adoption during onboarding and support",
      "Developed automated workflows reducing manual processes by up to 80%, improving efficiency across commercial, product and operations teams",
      "Implemented a modular portal for client engagement touchpoints, reducing manual follow-ups and enabling up to 70% fewer cross-channel interactions during onboarding/support",
      "Captured stakeholder feedback from 50+ early-stage engagements and translated insights into business cases, wireframes and simulation-based playbooks-informing iterative solution and product refinement"
    ],
    tags: ["Automation", "AI", "Design", "Solution", "Strategy"]
  },
  {
    id: "exp-2",
    company: "Zeus",
    role: "Business Acquisition Lead",
    period: "Jan 2023 - Dec 2024",
    location: "London, UK",
    type: "Full-time",
    description: "Owned new customer acquisition for SaaS products, managing full cycle from discovery to closing and onboarding.",
    highlights: [
      "Led discovery calls and stakeholder workshops to understand goals, pain points and success metrics prior to proposing solutions",
      "Built relationships with C-suite executives, positioning technology solutions as drivers of competitive advantage",
      "Contributed to product and go-to-market strategy with customer insights and competitive analysis",
      "Analysed sales data to forecast performance and identify high-value segments",
      "Created technical and commercial presentations for key accounts"
    ],
    tags: ["SaaS", "Strategy", "Customer Engagement", "Scaling", "Growth"]
  },
  {
    id: "exp-3",
    company: "Estrella Infotech",
    role: "Customer Relationship Manager - IT",
    period: "Jul 2020 - Aug 2021",
    location: "India",
    type: "Full-time",
    description: "Managed key customer accounts, serving as strategic point of contact for technology and software initiatives.",
    highlights: [
      "Developed account plans aligned with client priorities",
      "Advised on process improvements and technology leverage opportunities",
      "Monitored competitors and market trends for growth opportunities",
      "Oversaw multiple software development projects within budget and timeline"
    ],
    tags: ["Account Management", "Client Relations", "Project Management", "Process Optimisation"]
  },
  {
    id: "exp-4",
    company: "Estrella Infotech",
    role: "PHP Developer",
    period: "Jul 2019 - Jun 2020",
    location: "India",
    type: "Full-time",
    description: "Developed and maintained PHP-based websites and database-driven systems, including eCommerce platforms.",
    highlights: [
      "Developed PHP-based websites and eCommerce platforms for international clients",
      "Designed and optimised MySQL databases for scalable applications",
      "Recognised as 'Star Employee' three times for strong delivery quality"
    ],
    tags: ["PHP", "MySQL", "Web Development", "eCommerce", "Database Design"]
  }
];

export const skills = {
  technical: [
    { name: "Airtable", level: 95, category: "tools" },
    { name: "Figma", level: 85, category: "tools" },
    { name: "Jira", level: 90, category: "tools" },
    { name: "Confluence", level: 85, category: "tools" },
    { name: "Zapier", level: 90, category: "tools" },
    { name: "SQL", level: 85, category: "programming" },
    { name: "Python", level: 75, category: "programming" },
    { name: "HubSpot", level: 85, category: "tools" },
    { name: "Advanced Excel", level: 90, category: "data" },
    { name: "Power BI", level: 75, category: "data" },
    { name: "PHP", level: 70, category: "programming" },
    { name: "Data Analytics", level: 85, category: "data" }
  ],
  business: [
    { name: "Process Optimisation", level: 95, category: "operations" },
    { name: "Cross-Functional Collaboration", level: 95, category: "leadership" },
    { name: "Requirements Gathering & Workflow Mapping", level: 90, category: "product" },
    { name: "Rollout Support & Playbooks", level: 90, category: "enablement" },
    { name: "Wireframing (Figma)", level: 85, category: "design" },
    { name: "Structured Solution Design", level: 90, category: "strategy" },
    { name: "Digital Twin Modelling", level: 90, category: "innovation" },
    { name: "Workflow Automation", level: 95, category: "innovation" },
    { name: "Stakeholder Management", level: 95, category: "leadership" },
    { name: "Strategic Planning", level: 85, category: "strategy" },
    { name: "SaaS & B2B Solutions", level: 90, category: "domain" },
    { name: "KPI Development", level: 85, category: "analytics" },
    { name: "Go-to-Market Strategy", level: 80, category: "strategy" },
    { name: "Technical Project Management", level: 95, category: "delivery" }
  ]
};

export const education = [
  {
    id: "edu-1",
    degree: "Master's in Business Management",
    institution: "Queen Mary University of London",
    location: "London, UK",
    period: "2021 - 2022",
    details: "International Student Ambassador. Dissertation examined gender differences in online vs in-person purchasing and brand loyalty in fashion. Key modules: Strategy Management, Data-Led Product Lifecycle, Marketing Management, Research Methods for Management.",
    achievements: ["International Student Ambassador"]
  },
  {
    id: "edu-2",
    degree: "BTech in Computer Science Engineering",
    institution: "Amity University",
    location: "Noida, India",
    period: "2015 - 2019",
    details: "Comprehensive computer science engineering education with strong foundation in Python, SQL, Java, C++, C, and data structures. Built technical capabilities in algorithm design, software engineering principles, and systems thinking.",
    achievements: []
  },
  {
    id: "edu-3",
    degree: "Senior Certification Course",
    institution: "University of Florida",
    location: "USA",
    period: "2019",
    details: "Advanced Data Structures. Major project on VR & AI focusing on behavioural differences in human-robot collaboration using intelligent force support prototype in Virtual Reality.",
    achievements: ["VR & AI Research Project"]
  }
];

export const projects = [
  {
    id: "proj-1",
    title: "VR & AI Human-Robot Collaboration",
    category: "Research",
    description: "Examined behavioural differences in human-robot collaboration using an intelligent force support prototype in Virtual Reality.",
    institution: "University of Florida",
    year: "2019",
    tags: ["VR", "AI", "Robotics", "Research"],
    type: "academic",
    link: null
  },
  {
    id: "proj-2",
    title: "RADAR Signal Processing",
    category: "Research Paper",
    description: "Published research on combining technologies to remove background unwanted signals from RADAR images.",
    publication: "International Conference on Green Computing & Internet of Things",
    year: "2018",
    tags: ["Signal Processing", "RADAR", "IoT"],
    type: "publication",
    link: "https://ieeexplore.ieee.org/abstract/document/8753017/"
  },
  {
    id: "proj-3",
    title: "Analysis & Impact of Current CAPTCHA Approaches and its Significance",
    category: "Research Paper",
    description: "Published research analysing current CAPTCHA approaches and their significance in web security.",
    publication: "Conference on Computational Techniques, Electronics and Mechanical Systems",
    year: "2018",
    tags: ["Security", "Web Technologies", "UX"],
    type: "publication",
    link: "https://ieeexplore.ieee.org/abstract/document/8769168/"
  }
];

export const achievements = [
  {
    id: "ach-1",
    title: "Star Employee Award (3x)",
    organization: "Estrella Infotech",
    year: "2019-2020",
    description: "Recognised for strong client feedback, delivery quality, and overall contribution"
  },
  {
    id: "ach-2",
    title: "Published Researcher",
    organization: "International Conferences",
    year: "2018",
    description: "Two research papers published in international conferences on RADAR signal processing and CAPTCHA security"
  },
  {
    id: "ach-3",
    title: "International Student Ambassador",
    organization: "Queen Mary University of London",
    year: "2021-2022",
    description: "Represented the university and supported international student community"
  }
];

export const myWork = [
  {
    id: "work-1",
    title: "Digital Twin Implementation",
    category: "Solution Design",
    description: "Developed synthetic models to simulate operational workflows for 10+ multinational companies across mobility and retail sectors, reducing discovery time by 60%",
    impact: "60% faster discovery",
    tags: ["Digital Twins", "Workflow Design", "Stakeholder Demos"],
    metrics: {
      clients: "10+",
      timeReduction: "60%",
      industries: ["Mobility", "Retail", "Logistics"]
    }
  },
  {
    id: "work-2",
    title: "Automation Portal",
    category: "Product Development",
    description: "Built modular self-serve portal reducing manual client engagement touchpoints by 70%, streamlining onboarding and support operations",
    impact: "70% efficiency gain",
    tags: ["Automation", "Portal Development", "Client Experience"],
    metrics: {
      reduction: "70%",
      impactArea: "Cross-channel interactions",
      users: "Commercial, Product, Strategy teams"
    }
  },
  {
    id: "work-3",
    title: "Workflow Automation Framework",
    category: "Process Optimisation",
    description: "Designed and implemented automated workflows using Zapier, Python, and API integrations, reducing manual processes by 80% across operations",
    impact: "80% process automation",
    tags: ["Zapier", "Python", "API Integration"],
    metrics: {
      reduction: "80%",
      scope: "Manual processes",
      teams: ["Operations", "Commercial", "Product"]
    }
  },
  {
    id: "work-4",
    title: "Requirements Framework",
    category: "Product Strategy",
    description: "Created structured playbooks and operational blueprints translating stakeholder needs into wireframes and delivery outputs, standardising product delivery",
    impact: "Standardised delivery",
    tags: ["Requirements Gathering", "Figma", "Playbooks"],
    metrics: {
      engagements: "50+",
      deliverables: "Wireframes, Playbooks, Business Cases",
      outcome: "Iterative product refinement"
    }
  }
];

export const creationsAndLearnings = [
  {
    id: "musing-1",
    title: "Product Spec Builder",
    description: "An interactive tool that walks you through structuring product specifications - from problem framing to acceptance criteria.",
    url: "https://productspecbuilder.netlify.app/",
    category: "Tool",
    preview: "Build clearer specs, faster - guided structure for product thinkers",
    previewImage: null
  },
  {
    id: "musing-2",
    title: "Career Pivot Planner",
    description: "A guided planner for navigating intentional career pivots - mapping skills, gaps, and next moves with structure.",
    url: "https://careerpivotplanner.netlify.app/",
    category: "Interactive",
    preview: "Plan your pivot - structured prompts for honest self-reflection",
    previewImage: null
  },
  {
    id: "musing-3",
    title: "Learnings Around Requirements & Product Thinking",
    description: "Deep dive into requirement elicitation, research-backed insights, and practical frameworks for product thinking",
    url: "https://saniasrv7.github.io/Learnings-product_thinking/",
    category: "Product Thinking",
    preview: "8 key insights on requirements gathering, context, and strategic decision-making",
    previewImage: "https://saniasrv7.github.io/Learnings-product_thinking/og-image.png"
  },
  {
    id: "musing-4",
    title: "Product Thinking Challenge",
    description: "An interactive challenge to test and improve your product thinking skills through real-world scenarios",
    url: "https://saniasrv7.github.io/Product-challenge/",
    category: "Interactive",
    preview: "Put your product decisions to the test",
    previewImage: "https://saniasrv7.github.io/Product-challenge/og-image.png"
  },
  {
    id: "musing-5",
    title: "Reporting Visualiser",
    description: "A tool to visualise and understand reporting structures, data flows, and stakeholder communication patterns",
    url: "https://saniasrv7.github.io/Reporting-Visualiser/",
    category: "Tool",
    preview: "Interactive visualisation for better data comprehension",
    previewImage: "https://saniasrv7.github.io/Reporting-Visualiser/og-image.png"
  },
  {
    id: "musing-6",
    title: "50 Stakeholder Conversations: What I Learned",
    description: "Real insights from 50+ stakeholder conversations across different industries, roles, and contexts",
    url: "https://saniasrv7.github.io/Blog50conversations/",
    category: "Reflections",
    preview: "Patterns, surprises, and lessons from real conversations",
    previewImage: "https://saniasrv7.github.io/Blog50conversations/og-image.png"
  }
];

export const testimonials = [
  {
    id: "test-1",
    name: "Sam Mcguirk",
    role: "Chief Commercial & Sales Officer",
    company: "Zeus",
    content: "Sania has been instrumental in bridging the gap between our commercial goals and technical execution. Her ability to translate complex business requirements into actionable solutions is exceptional.",
    image: null
  }
];
